/**
 * The app's single TanStack QueryClient.
 *
 * Extracted from main.tsx so it can be imported by non-React code (the apiClient
 * and auth accessors read/write the auth/me cache imperatively).
 *
 * Deliberately dependency-light: it does NOT import the error-routing layer.
 * main.tsx wires the cache `onError` handlers via `wireAppQueryClient()`
 * (serverErrorRouting.ts → `wireQueryErrorRouting()`) after import. Importing serverErrorRouting here would pull it (and reportClientError)
 * into every module that imports the queryClient, loading it before a test's
 * `vi.mock('@/api/clientErrors')` can apply — which silently breaks that mock.
 */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import type { DefaultOptions, Query } from '@tanstack/react-query';
// Dependency-light (imports only axios), so the note above still holds.
import { isTransportFailure } from '@/api/failureClass';

const queryCache = new QueryCache();
const mutationCache = new MutationCache();

/**
 * The app's TanStack query retry rule (#774). Retry ownership is split, never
 * stacked:
 *   - `client.ts` owns retry for RESPONSES: the status-based budget
 *     (RETRY_CONFIG, 1 + maxRetries requests on a retryable 5xx) and the
 *     per-endpoint circuit. A TanStack retry on top multiplied it (8 requests
 *     per query on a deterministic 5xx, vingapi#1210).
 *   - TanStack retries ONCE only what `client.ts` never retries: a transport
 *     failure with no response on a non-consume request. That pending retry is
 *     what keeps the #239c inline "unreachable" screen: `markUnreachable()`
 *     sets onlineManager offline, which PAUSES it, so the query stays
 *     `pending`/`paused` and LoadingState renders UnreachableState instead of
 *     the page's error state.
 * A circuit-breaker cancel classifies as `other`, so an open circuit gets no
 * TanStack retry either.
 */
export function retryTransportFailureOnce(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && isTransportFailure(error);
}

/**
 * Production query defaults. Exported so tests that must build their own
 * QueryClient (e.g. with a wired QueryCache) run on the SAME retry rule rather
 * than a hand-copied one that silently drifts from production.
 */
export const queryDefaults = {
  staleTime: 1000 * 60 * 5, // 5 minutes
  retry: retryTransportFailureOnce,
  refetchOnWindowFocus: false,
} satisfies NonNullable<DefaultOptions['queries']>;

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: queryDefaults,
  },
});

/**
 * Mutation `meta` flag: the page that owns the action presents its failure, so
 * the central router reports it (crash report) but pushes no global banner
 * (Q-506 card 1571: the ctx-param gate's failed page).
 */
export const PAGE_OWNS_FAILURE_META = 'pageOwnsFailure';

/**
 * Mutation `meta` flag (#1685, owner Q-550): this surface shows a known refusal
 * as a FAILURE (not its own refusal UX), so the central router reports it even
 * though the registry exempts it by endpoint. The context switcher sets it: its
 * `context_not_member` 403 means a stale list or a server fault, and the
 * "Error reference" it shows must match an alert. The `?ctx=` gate does not.
 */
export const REPORT_KNOWN_REFUSAL_META = 'reportKnownRefusal';

/**
 * Query `meta` flag: the region that owns the request shows its failure inside
 * itself (Q-506 card 1542: the breakdown panel's "Couldn't load this."). The
 * central router still reports the failure (crash report with its trace id) but
 * shows neither the full-page failure screen nor the global banner. Any later
 * region uses the same flag: `meta: REGION_SHOWS_FAILURE_META`.
 */
export const FAILURE_SHOWN_IN_META = 'failureShownIn';
export const REGION_SHOWS_FAILURE_META = { [FAILURE_SHOWN_IN_META]: 'region' } as const;

/**
 * Wire the central error routing onto the cache handlers. Called once, via
 * `wireAppQueryClient()` in serverErrorRouting.ts, from main.tsx (kept out of this module so it stays free of the serverErrorRouting
 * import graph).
 */
export function wireQueryErrorRouting(
  onQueryError: (error: unknown, query: Query<unknown, unknown>) => void,
  onMutationError: NonNullable<ConstructorParameters<typeof MutationCache>[0]>['onError'],
): void {
  queryCache.config.onError = onQueryError;
  mutationCache.config.onError = onMutationError;
}
